/** Calendar-date helpers on 'YYYY-MM-DD' strings, done in UTC so the browser's timezone never shifts a day. */
const toUtc = (d: string) => new Date(`${d}T00:00:00Z`);
const fmt = (d: Date) => d.toISOString().slice(0, 10);

/** A country code to the locale its dates read most naturally in. Falls back to en-AU, this app's origin. */
const LOCALE_FOR_COUNTRY: Record<string, string> = {
  AU: 'en-AU',
  NZ: 'en-NZ',
  US: 'en-US',
  GB: 'en-GB',
  CA: 'en-CA',
  IE: 'en-IE',
};

/**
 * The locale every date on screen formats in, set once the signed-in business's
 * country is known (see useBrand in Brand.tsx). Defaults to en-AU so a page
 * rendered before that arrives, or with no country set, reads as it always has.
 */
let locale = 'en-AU';
export const setLocaleForCountry = (country: string) => {
  locale = LOCALE_FOR_COUNTRY[country.toUpperCase()] ?? 'en-AU';
};
/** For a page that formats a date itself instead of going through dayLabel/dateLabel. */
export const currentLocale = () => locale;

export const addDays = (d: string, n: number) => fmt(new Date(toUtc(d).getTime() + n * 86_400_000));

/** The Monday on or before a date (weeks run Monday to Sunday). */
export function mondayOf(d: string): string {
  const iso = ((toUtc(d).getUTCDay() + 6) % 7) + 1; // Mon=1..Sun=7
  return addDays(d, 1 - iso);
}

export const todayLocal = () => {
  const n = new Date();
  return `${n.getFullYear()}-${String(n.getMonth() + 1).padStart(2, '0')}-${String(n.getDate()).padStart(2, '0')}`;
};

export const dayLabel = (d: string) =>
  toUtc(d).toLocaleDateString(locale, {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    timeZone: 'UTC',
  });

/** Just the weekday, short: "Mon". For a column heading that shows the date number beside it. */
export const weekdayLabel = (d: string) =>
  toUtc(d).toLocaleDateString(locale, { weekday: 'short', timeZone: 'UTC' });

/** A date with its year, for lists that span more than a week: "13 Sept 2026". */
export const dateLabel = (d: string) =>
  toUtc(d).toLocaleDateString(locale, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    timeZone: 'UTC',
  });
